/**
 * Learning (AI Tutor) API client (Sprint 7 B5).
 *
 * Mirror backend endpoints:
 *   GET    /api/learning/conversations/?lesson=<slug>          — list per-user
 *   POST   /api/learning/conversations/                        — create
 *   GET    /api/learning/conversations/<id>/messages/          — paginated
 *   POST   /api/learning/conversations/<id>/messages/send/     — SSE stream
 *   POST   /api/learning/embeddings/refresh/<course_slug>/     — admin
 *
 * SSE streaming dùng `fetch + ReadableStream + TextDecoder` (không EventSource
 * vì cần pass Authorization header). Pattern: parse `data: {...}\n\n` per line.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "./client";

// ─── Types ────────────────────────────────────────────────────────────────

export type MessageRole = "user" | "assistant" | "system";

export interface Citation {
  lesson_id: number;
  lesson_slug: string;
  lesson_title: string;
  chunk_order: number;
}

export interface Message {
  id: number;
  role: MessageRole;
  content: string;
  tokens_input: number;
  tokens_output: number;
  citations: Citation[];
  llm_provider: string;
  llm_model: string;
  created_at: string;
}

export interface LessonBrief {
  id: number;
  slug: string;
  title: string;
}

export interface Conversation {
  id: number;
  title: string;
  lesson: LessonBrief | null;
  message_count: number;
  started_at: string;
  last_message_at: string;
}

export interface Paginated<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

// SSE event types từ backend
export type SSEEvent =
  | { type: "content"; delta: string }
  | { type: "citations"; citations: Citation[] }
  | { type: "done"; tokens_input: number; tokens_output: number; provider: string; model: string }
  | { type: "error"; detail: string };

// ─── Hooks ────────────────────────────────────────────────────────────────

// ─── Sprint 16 B2 — Adaptive recommendation types + hook ──────────────────

export interface RecommendationTopic {
  lesson_id: number;
  lesson_title: string;
  citation_count?: number; // chỉ có trong strong_topics
}

export interface SuggestedCourse {
  course_id: number;
  course_code: string;
  course_name: string;
  course_slug: string;
}

export interface RecommendationsResponse {
  strong_topics: RecommendationTopic[];
  weak_topics: RecommendationTopic[];
  /** Sprint 17 B1 — courses user enrolled chưa chat lesson nào */
  suggested_courses: SuggestedCourse[];
  /**
   * Sprint 20 B1 — backend flag RECOMMENDATIONS_USE_DECAY.
   * True → `citation_count` là float weighted theo velocity decay
   *        (half-life 30d). FE render decay badge + tooltip giải thích.
   * False → `citation_count` raw int, không hiển thị badge.
   */
  decay_enabled: boolean;
}

export function useMyRecommendations(courseSlug?: string) {
  return useQuery<RecommendationsResponse>({
    queryKey: ["recommendations-me", courseSlug ?? null],
    queryFn: async () => {
      const params = courseSlug ? { course: courseSlug } : undefined;
      const res = await apiClient.get<RecommendationsResponse>(
        "/learning/recommendations/me/",
        { params },
      );
      return res.data;
    },
    staleTime: 60_000, // recommendation đổi chậm, cache 1 phút
  });
}


export function useConversations(lessonSlug?: string) {
  return useQuery({
    queryKey: ["learning", "conversations", lessonSlug ?? ""],
    queryFn: async () => {
      const params = lessonSlug ? { lesson: lessonSlug } : {};
      const resp = await apiClient.get<Conversation[]>("/learning/conversations/", { params });
      return resp.data;
    },
    staleTime: 30_000,
  });
}

export function useCreateConversation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (lessonSlug?: string | null) => {
      const resp = await apiClient.post<Conversation>("/learning/conversations/", {
        lesson_slug: lessonSlug ?? null,
      });
      return resp.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["learning", "conversations"] });
    },
  });
}

export function useMessages(conversationId: number | undefined) {
  return useQuery({
    queryKey: ["learning", "messages", conversationId],
    queryFn: async () => {
      if (!conversationId) return { results: [], count: 0, next: null, previous: null };
      const resp = await apiClient.get<Paginated<Message> | Message[]>(
        `/learning/conversations/${conversationId}/messages/`,
      );
      // Backend trả paginated dict, có thể trả array nếu pagination_class=None future
      if (Array.isArray(resp.data)) {
        return { results: resp.data, count: resp.data.length, next: null, previous: null };
      }
      return resp.data;
    },
    enabled: !!conversationId,
    staleTime: 5_000,
  });
}

// ─── SSE Streaming ────────────────────────────────────────────────────────

/**
 * Gửi message + nhận SSE stream. Trả async iterator events.
 *
 * Em dùng fetch thay EventSource vì cần Authorization header (EventSource API
 * không hỗ trợ custom headers — workaround duy nhất là pass token qua query
 * string, không an toàn).
 */
export async function* sendMessageStream(
  conversationId: number,
  content: string,
): AsyncGenerator<SSEEvent, void, void> {
  const baseURL =
    (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "/api";

  const tenantSlug =
    (import.meta.env.VITE_DEFAULT_TENANT as string | undefined) ??
    window.localStorage.getItem("tenant_slug") ??
    "";

  const authRaw = window.localStorage.getItem("sep2026-auth");
  let access = "";
  if (authRaw) {
    try {
      access = JSON.parse(authRaw)?.state?.accessToken ?? "";
    } catch {
      // ignore
    }
  }

  const resp = await fetch(
    `${baseURL}/learning/conversations/${conversationId}/messages/send/`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Tenant-Slug": tenantSlug,
        Authorization: access ? `Bearer ${access}` : "",
      },
      body: JSON.stringify({ content }),
    },
  );

  if (!resp.ok) {
    let errBody = "";
    try {
      errBody = await resp.text();
    } catch {
      /* ignore */
    }
    throw new Error(`SSE failed ${resp.status}: ${errBody}`);
  }

  const reader = resp.body?.getReader();
  if (!reader) throw new Error("No response body stream");

  const decoder = new TextDecoder("utf-8");
  let buffer = "";

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    // SSE events separator = double-newline
    const lines = buffer.split("\n\n");
    buffer = lines.pop() ?? ""; // last partial line giữ lại buffer

    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed.startsWith("data: ")) continue;
      const json = trimmed.slice("data: ".length);
      try {
        yield JSON.parse(json) as SSEEvent;
      } catch (err) {
        console.error("[SSE] invalid JSON:", json, err);
      }
    }
  }
}

// ─── SRS (Pha 3 — spaced repetition) ────────────────────────────────────────

export interface SrsReview {
  id: number;
  lesson_slug: string;
  front_ja: string;
  back_vi: string;
  ease_factor: number;
  interval_days: number;
  repetitions: number;
  due_at: string;
  last_reviewed_at: string | null;
}

export interface GradeSrsInput {
  lessonSlug: string;
  frontJa: string;
  backVi?: string;
  quality: number; // 0–5 (SM-2). FE map: Quên=1, Tạm=3, Dễ=5.
}

/** POST /api/learning/srs/review/ — chấm 1 thẻ SRS, trả trạng thái lịch mới. */
export function useGradeSrsCard() {
  return useMutation({
    mutationFn: async ({ lessonSlug, frontJa, backVi, quality }: GradeSrsInput) => {
      const res = await apiClient.post<SrsReview>("/learning/srs/review/", {
        lesson_slug: lessonSlug,
        front_ja: frontJa,
        back_vi: backVi ?? "",
        quality,
      });
      return res.data;
    },
  });
}

/**
 * GET /api/learning/srs/due/?course=<slug> — thẻ đến hạn ôn của học viên.
 *
 * Dùng cho trang "Ôn tập tổng hợp" theo khoá (gom thẻ đến hạn toàn khoá, thay
 * vì chỉ ôn trong từng bài). `courseSlug` undefined → ôn toàn bộ (mọi khoá).
 * Chỉ trả thẻ đã có lịch (học viên từng chấm ≥1 lần) và due_at ≤ giờ hiện tại.
 */
export function useSrsDue(courseSlug?: string) {
  return useQuery<SrsReview[]>({
    queryKey: ["srs-due", courseSlug ?? null],
    queryFn: async () => {
      const params = courseSlug ? { course: courseSlug } : undefined;
      const res = await apiClient.get<SrsReview[]>("/learning/srs/due/", { params });
      return res.data;
    },
    staleTime: 10_000,
  });
}

// ─── Speaking (Pha 3 — luyện nói + chấm phát âm; PII → consent gate) ──────────

export interface SpeakingConsent {
  policy_version: string;
  guardian_name?: string;
  granted_at?: string;
  revoked_at?: string | null;
  is_active: boolean;
}

export interface SpeakingScoreResult {
  score: number;
  transcript: string;
  target_ja: string;
}

/** Response POST attempts: sync (có score) hoặc async (có job_id). */
export type SpeakingScoreResponse = SpeakingScoreResult | { job_id: string; status: string };

/** Kết quả poll async. */
export interface SpeakingPollResult {
  status: "pending" | "done" | "error";
  score?: number;
  transcript?: string;
  detail?: string;
}

/** GET /api/learning/speaking/consent/ — trạng thái consent luyện nói. */
export function useSpeakingConsent() {
  return useQuery<SpeakingConsent>({
    queryKey: ["speaking-consent"],
    queryFn: async () => {
      const res = await apiClient.get<SpeakingConsent>("/learning/speaking/consent/");
      return res.data;
    },
    staleTime: 60_000,
  });
}

/** POST consent — đồng ý (kèm tên giám hộ nếu <18). */
export function useGrantSpeakingConsent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (guardianName?: string) => {
      const res = await apiClient.post<SpeakingConsent>("/learning/speaking/consent/", {
        agree: true,
        guardian_name: guardianName ?? "",
      });
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["speaking-consent"] }),
  });
}

/** POST attempts (multipart) — chấm 1 lượt. Trả sync {score} hoặc async {job_id}. */
export function useScoreSpeaking() {
  return useMutation({
    mutationFn: async (input: {
      lessonSlug: string;
      promptIndex: number;
      targetJa: string;
      audio: Blob;
    }) => {
      const form = new FormData();
      form.append("audio", input.audio, "rec.webm");
      form.append("lesson_slug", input.lessonSlug);
      form.append("prompt_index", String(input.promptIndex));
      form.append("target_ja", input.targetJa);
      const res = await apiClient.post<SpeakingScoreResponse>(
        "/learning/speaking/attempts/",
        form,
      );
      return res.data;
    },
  });
}

/** GET poll kết quả chấm async (Nấc 2). */
export async function fetchSpeakingResult(jobId: string): Promise<SpeakingPollResult> {
  const res = await apiClient.get<SpeakingPollResult>(
    `/learning/speaking/attempts/${jobId}/result/`,
  );
  return res.data;
}

/** DELETE — xoá toàn bộ dữ liệu luyện nói + rút consent. */
export function useDeleteSpeakingData() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const res = await apiClient.delete("/learning/speaking/data/mine/");
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["speaking-consent"] }),
  });
}
